import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ENV } from '../config/env';
import toast from 'react-hot-toast';

interface BusinessPlan {
  id: string;
  business_name: string;
  tagline?: string;
  problem_statement?: string;
  solution_description?: string;
  industry?: string;
}

interface SavedPitchDeck {
  id: string;
  company_name: string;
  presentation_url: string;
  download_url: string;
  theme: string;
  created_at: string;
}

const PitchDeckGenerator: React.FC = () => {
  const { user, getToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [businessPlans, setBusinessPlans] = useState<BusinessPlan[]>([]);
  const [savedDecks, setSavedDecks] = useState<SavedPitchDeck[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [mode, setMode] = useState<'import' | 'manual' | 'guide'>('import');
  const [theme, setTheme] = useState<'tech' | 'finance' | 'consumer'>('tech');
  const [includeImages, setIncludeImages] = useState(false);
  const [textGuide, setTextGuide] = useState<string>('');

  // Manual form data
  const [formData, setFormData] = useState({
    name: '',
    tagline: '',
    problem: '',
    solution: '',
    industry: 'Technology',
    marketSize: { tam: '', sam: '', som: '' },
    businessModel: '',
    traction: '',
    competition: '',
    team: [] as Array<{ name: string; role: string; bio: string }>,
    financials: { fundingAsk: '', useOfFunds: '' }
  });

  // Load business plans and saved decks on mount
  useEffect(() => {
    loadBusinessPlans();
    loadSavedDecks();
    
    // Check if we navigated from a business plan
    const state = (location as any)?.state;
    if (state?.fromBusinessPlan && state?.planId) {
      setMode('import');
      setSelectedPlanId(state.planId);
    }
  }, []);

  const loadBusinessPlans = async () => {
    try {
      const token = await getToken();
      const response = await fetch(`${ENV.API_URL}/business-plans`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // API returns array directly, not wrapped in businessPlans
        setBusinessPlans(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error loading business plans:', error);
    }
  };

  const loadSavedDecks = async () => {
    try {
      const token = await getToken();
      const response = await fetch(`${ENV.API_URL}/pitch-deck/my-decks`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSavedDecks(data.pitchDecks || []);
      }
    } catch (error) {
      console.error('Error loading saved decks:', error);
    }
  };

  const handleGenerateFromPlan = async () => {
    if (!selectedPlanId) {
      toast.error('Please select a business plan');
      return;
    }

    setLoading(true);
    try {
      const token = await getToken();
      const response = await fetch(`${ENV.API_URL}/pitch-deck/generate-from-plan/${selectedPlanId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ theme, includeImages })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success('Pitch deck generated successfully!');
        
        // Reload saved decks
        await loadSavedDecks();
        
        // Open Google Slides in new tab
        window.open(data.pitchDeck.url, '_blank');
        
        // Show download option
        setTimeout(() => {
          const downloadConfirm = window.confirm('Would you like to download the presentation as PPTX?');
          if (downloadConfirm) {
            window.open(data.pitchDeck.downloadUrl, '_blank');
          }
        }, 1000);
      } else {
        toast.error(data.error || 'Failed to generate pitch deck');
      }
    } catch (error) {
      console.error('Error generating pitch deck:', error);
      toast.error('Failed to generate pitch deck');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateManual = async () => {
    if (!formData.name || !formData.problem || !formData.solution) {
      toast.error('Please fill in required fields: Company Name, Problem, and Solution');
      return;
    }

    setLoading(true);
    try {
      const token = await getToken();
      const response = await fetch(`${ENV.API_URL}/pitch-deck/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ ...formData, theme, includeImages })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success('Pitch deck generated successfully!');
        
        // Reload saved decks
        await loadSavedDecks();
        
        // Open Google Slides
        window.open(data.pitchDeck.url, '_blank');
        
        // Download option
        setTimeout(() => {
          const downloadConfirm = window.confirm('Would you like to download as PPTX?');
          if (downloadConfirm) {
            window.open(data.pitchDeck.downloadUrl, '_blank');
          }
        }, 1000);
      } else {
        toast.error(data.error || 'Failed to generate pitch deck');
      }
    } catch (error) {
      console.error('Error generating pitch deck:', error);
      toast.error('Failed to generate pitch deck');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateFromGuide = async () => {
    if (!textGuide.trim()) {
      toast.error('Please provide text guidance for the pitch deck');
      return;
    }

    setLoading(true);
    try {
      const token = await getToken();
      const response = await fetch(`${ENV.API_URL}/pitch-deck/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ 
          name: 'Pitch Deck',
          problem: textGuide,
          solution: 'As described in guide',
          textGuide,
          theme, 
          includeImages 
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success('Pitch deck generated successfully!');
        
        // Reload saved decks
        await loadSavedDecks();
        
        // Open Google Slides
        window.open(data.pitchDeck.url, '_blank');
        
        // Download option
        setTimeout(() => {
          const downloadConfirm = window.confirm('Would you like to download as PPTX?');
          if (downloadConfirm) {
            window.open(data.pitchDeck.downloadUrl, '_blank');
          }
        }, 1000);
      } else {
        toast.error(data.error || 'Failed to generate pitch deck');
      }
    } catch (error) {
      console.error('Error generating pitch deck:', error);
      toast.error('Failed to generate pitch deck');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === 'text/plain') {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        setTextGuide(text);
        toast.success('File loaded successfully');
      };
      reader.readAsText(file);
    } else {
      toast.error('Please upload a .txt file');
    }
  };

  const handleDeleteDeck = async (deckId: string) => {
    if (!window.confirm('Are you sure you want to delete this pitch deck?')) {
      return;
    }

    try {
      const token = await getToken();
      const response = await fetch(`${ENV.API_URL}/pitch-deck/${deckId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('Pitch deck deleted');
        await loadSavedDecks();
      } else {
        toast.error('Failed to delete pitch deck');
      }
    } catch (error) {
      console.error('Error deleting pitch deck:', error);
      toast.error('Failed to delete pitch deck');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Pitch Deck Generator</h1>
          <p className="text-gray-600">
            Generate professional investor pitch decks using AI-powered content and Google Slides
          </p>
        </div>

        {/* Saved Pitch Decks */}
        {savedDecks.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h2 className="text-lg font-semibold mb-4">Your Saved Pitch Decks ({savedDecks.length})</h2>
            <div className="space-y-3">
              {savedDecks.map((deck) => (
                <div key={deck.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-blue-300 transition">
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900">{deck.company_name}</h3>
                    <p className="text-sm text-gray-500">
                      Theme: {deck.theme} • Created: {new Date(deck.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => window.open(deck.presentation_url, '_blank')}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm"
                    >
                      Open
                    </button>
                    <button
                      onClick={() => window.open(deck.download_url, '_blank')}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm"
                    >
                      Download
                    </button>
                    <button
                      onClick={() => handleDeleteDeck(deck.id)}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-sm"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Mode Selector */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Choose Generation Method</h2>
          <div className="grid grid-cols-3 gap-4">
            <button
              onClick={() => setMode('import')}
              className={`py-4 px-6 rounded-lg border-2 transition ${
                mode === 'import'
                  ? 'border-blue-600 bg-blue-50 text-blue-700'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="text-2xl mb-2">📄</div>
              <div className="font-semibold">Import Business Plan</div>
              <div className="text-sm text-gray-500 mt-1">Use existing plan data</div>
            </button>
            <button
              onClick={() => setMode('manual')}
              className={`py-4 px-6 rounded-lg border-2 transition ${
                mode === 'manual'
                  ? 'border-blue-600 bg-blue-50 text-blue-700'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="text-2xl mb-2">✏️</div>
              <div className="font-semibold">Enter Manually</div>
              <div className="text-sm text-gray-500 mt-1">Fill in details yourself</div>
            </button>
            <button
              onClick={() => setMode('guide')}
              className={`py-4 px-6 rounded-lg border-2 transition ${
                mode === 'guide'
                  ? 'border-blue-600 bg-blue-50 text-blue-700'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="text-2xl mb-2">📝</div>
              <div className="font-semibold">Upload Guide</div>
              <div className="text-sm text-gray-500 mt-1">Paste or upload text</div>
            </button>
          </div>
        </div>

        {/* Theme Selector */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Select Theme</h2>
          <div className="grid grid-cols-3 gap-4">
            <button
              onClick={() => setTheme('tech')}
              className={`p-4 rounded-lg border-2 transition ${
                theme === 'tech'
                  ? 'border-blue-600 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="font-semibold">Tech</div>
              <div className="text-sm text-gray-500 mt-1">Modern, clean, innovative</div>
              <div className="flex gap-2 mt-2">
                <div className="w-6 h-6 rounded bg-blue-600"></div>
                <div className="w-6 h-6 rounded bg-cyan-500"></div>
                <div className="w-6 h-6 rounded bg-green-500"></div>
              </div>
            </button>
            <button
              onClick={() => setTheme('finance')}
              className={`p-4 rounded-lg border-2 transition ${
                theme === 'finance'
                  ? 'border-blue-600 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="font-semibold">Finance</div>
              <div className="text-sm text-gray-500 mt-1">Professional, trustworthy</div>
              <div className="flex gap-2 mt-2">
                <div className="w-6 h-6 rounded bg-indigo-900"></div>
                <div className="w-6 h-6 rounded bg-teal-600"></div>
                <div className="w-6 h-6 rounded bg-slate-600"></div>
              </div>
            </button>
            <button
              onClick={() => setTheme('consumer')}
              className={`p-4 rounded-lg border-2 transition ${
                theme === 'consumer'
                  ? 'border-blue-600 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="font-semibold">Consumer</div>
              <div className="text-sm text-gray-500 mt-1">Friendly, vibrant</div>
              <div className="flex gap-2 mt-2">
                <div className="w-6 h-6 rounded bg-pink-600"></div>
                <div className="w-6 h-6 rounded bg-orange-500"></div>
                <div className="w-6 h-6 rounded bg-purple-600"></div>
              </div>
            </button>
          </div>
        </div>

        {/* Import Mode */}
        {mode === 'import' && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h2 className="text-lg font-semibold mb-4">Select Business Plan</h2>
            {businessPlans.length > 0 ? (
              <select
                value={selectedPlanId}
                onChange={(e) => setSelectedPlanId(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">-- Select a business plan --</option>
                {businessPlans.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.business_name} {plan.tagline ? `- ${plan.tagline}` : ''}
                  </option>
                ))}
              </select>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No business plans found. <button onClick={() => navigate('/business-plan')} className="text-blue-600 hover:underline">Create one first</button>
              </div>
            )}
          </div>
        )}

        {/* Manual Mode */}
        {mode === 'manual' && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h2 className="text-lg font-semibold mb-4">Enter Business Information</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Acme Inc."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tagline
                </label>
                <input
                  type="text"
                  value={formData.tagline}
                  onChange={(e) => setFormData({ ...formData, tagline: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Revolutionizing how businesses..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Problem Statement *
                </label>
                <textarea
                  value={formData.problem}
                  onChange={(e) => setFormData({ ...formData, problem: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="What problem are you solving?"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Solution *
                </label>
                <textarea
                  value={formData.solution}
                  onChange={(e) => setFormData({ ...formData, solution: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="How do you solve it?"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Industry
                </label>
                <select
                  value={formData.industry}
                  onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option>Technology</option>
                  <option>Finance</option>
                  <option>Healthcare</option>
                  <option>Education</option>
                  <option>E-commerce</option>
                  <option>SaaS</option>
                  <option>Consumer</option>
                  <option>B2B</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Guide Mode */}
        {mode === 'guide' && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h2 className="text-lg font-semibold mb-4">Provide Pitch Deck Guidance</h2>
            <p className="text-sm text-gray-600 mb-4">
              Paste your pitch deck outline or upload a .txt file with details about your business, problem, solution, market, etc. The AI will use this to generate your deck.
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pitch Deck Guide Text
                </label>
                <textarea
                  value={textGuide}
                  onChange={(e) => setTextGuide(e.target.value)}
                  rows={12}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  placeholder="Example:&#10;&#10;Company: TechFlow Inc&#10;Tagline: Streamlining workflow automation&#10;&#10;Problem:&#10;- Teams waste 20 hours/week on manual tasks&#10;- Current tools are complex and expensive&#10;&#10;Solution:&#10;- AI-powered automation platform&#10;- No-code interface&#10;- 10x faster setup&#10;&#10;Market:&#10;- $50B TAM, $5B SAM&#10;- 5,000+ companies in our segment&#10;&#10;... (include as much detail as you want)"
                />
              </div>
              
              <div className="border-t border-gray-200 pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Or Upload a Text File (.txt)
                </label>
                <input
                  type="file"
                  accept=".txt"
                  onChange={handleFileUpload}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Upload a .txt file with your pitch deck content and the AI will structure it into slides
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Options */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Advanced Options</h2>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={includeImages}
              onChange={(e) => setIncludeImages(e.target.checked)}
              className="mr-2 h-4 w-4 text-blue-600"
            />
            <span className="text-sm text-gray-700">
              Generate custom images with AI (Ideogram) - Takes longer but looks better
            </span>
          </label>
        </div>

        {/* Generate Button */}
        <div className="flex gap-4">
          <button
            onClick={() => {
              if (mode === 'import') handleGenerateFromPlan();
              else if (mode === 'manual') handleGenerateManual();
              else if (mode === 'guide') handleGenerateFromGuide();
            }}
            disabled={loading || (mode === 'import' && !selectedPlanId) || (mode === 'guide' && !textGuide.trim())}
            className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-4 px-6 rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Generating Pitch Deck...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Generate Pitch Deck (10 slides)
              </>
            )}
          </button>
        </div>

        {/* Info Box */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex">
            <svg className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="text-sm text-blue-800">
              <strong>What you'll get:</strong> A 10-slide investor pitch deck including Cover, Problem, Solution, Market Size, Business Model, Traction, Competition, Team, Financials, and Ask slides. The presentation will open in Google Slides where you can edit and share it.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PitchDeckGenerator;
